/**
 * The CUSTOMER REMINDER DRAFT QUEUE's wire layer — U2 / 5.6's web half.
 *
 * ===========================================================================
 * TRANSCRIBED FROM THE SERVICE, NOT GUESSED
 * ===========================================================================
 * `RenewalDraftRow`, `RenewalDraftRecipient` and `RenewalDraftSendResult` are copied
 * field-for-field from `apps/api/src/modules/renewals/renewal-drafts.service.ts`, and the
 * envelope from `renewals.controller.ts`'s `RenewalDraftQueuePage`.
 *
 * ===========================================================================
 * ⚠ EVERY ROW IN THE QUEUE RESPONSE IS `state = 'drafted'` — AND A SENT HISTORY NOW EXISTS
 * ===========================================================================
 * The service's `CUSTOMER_DRAFT` predicate is `audience = 'customer' AND state = 'drafted'`,
 * so the queue is BY CONSTRUCTION the set of reminders nobody has sent. A successful send
 * removes the row from the next read.
 *
 * ⚠ RULE-7 CALLOUT — THIS BLOCK USED TO END "THERE IS NO SENT HISTORY", AND THAT SENTENCE
 * IS NOW FALSE. U2/5.6b adds `GET /countries/:cc/renewal-reminders/sent` and its per-party
 * sibling, so what went out is a SERVER fact readable by anyone with `quotes.view` — not, as
 * it was, the set of POST responses one browser tab happens to remember. The correction is
 * made deliberately rather than left standing: this repo treats these headers as
 * load-bearing, and a comment asserting an absent capability is how the next reader
 * re-invents the session-only panel that has just been removed.
 *
 * "Drafted but never sent" is still the state migration 27 was authorised for (rule 11) and
 * is still expressed as *the whole queue* rather than as a column. What changed is the other
 * half: "sent, and here is when and to whom" is now expressible too, from the same ledger.
 *
 * ===========================================================================
 * ⚠ MONEY CROSSES THIS BOUNDARY — AND SO DOES A RENDERED LETTER
 * ===========================================================================
 * `line_total` is behind `amounts.view`; a seat without it receives the literal string
 * `'•••'` — NOT null, NOT zero — so every consumer must go through `isMaskedAmount` /
 * `formatMoney` before it touches a number (`features/renewals/api.ts` argues this at
 * length for the register, and the argument is identical).
 *
 * THE SHARPER RULE IS `mail`. The drafted body is in the SAME response, and the amounts
 * interceptor masks FIELDS — it cannot see inside a sentence. `customer-reminder-mail.ts`
 * therefore carries no amount, no currency and no internal identifier, and the API asserts
 * the figure is absent from the whole serialisation for a masked seat. The client's own
 * obligation follows from that: the preview may render `mail.subject` and `mail.body` and
 * NOTHING ELSE off the row. Composing the value into the preview here would reproduce the
 * exact leak the server template was written to avoid — the mask still visibly working two
 * lines above a figure the client pasted in. `renewal-drafts.test.tsx` pins it.
 */

import { ApiRequestError, ApiTransportError, apiRequest } from '../../lib/api';
// The register's file OWNS the two source vocabularies for this app — see its header on why
// six inline `'so' | 'quote'` copies were drift with no gate. Imported, never re-typed.
import type { RenewalDocumentType, RenewalSource } from './api';

/* ─────────────────────────────────────────────────────────────────── DTOs ── */

/**
 * Who this letter goes to. NEVER a user — `customer-recipients.ts` reads `party_contacts`
 * and has no join to `users`, `roles` or the grp flag, and the API's separation spec fails
 * if that stops being true.
 */
export interface RenewalDraftRecipient {
  /** `party_contacts.id`, or null when that contact row has since been deleted. */
  contact_id: string | null;
  /** The address SNAPSHOTTED at draft time. This is where it will actually go. */
  email: string;
  name: string | null;
}

/** The drafted mail, rendered by the same pure function the send uses. */
export interface RenewalDraftMail {
  subject: string;
  body: string;
}

/** `RenewalDraftRow` — renewal-drafts.service.ts. */
export interface RenewalDraftRow {
  /** `renewal_reminders.id` — what the Send action names. */
  id: string;
  /** Which table `line_id` indexes. `so_lines.id` 77 and `quote_lines.id` 77 differ. */
  source: RenewalSource;
  line_id: string;
  country_id: number;
  /** What the drilldown opens. A Tally Sales subscription's document IS a quote (U5.4). */
  document_type: RenewalDocumentType;
  document_id: string;
  document_doc_no: string;
  party_id: string;
  party_name: string;
  product_id: string;
  product_name: string;
  /** The contract's expiry AS IT STANDS NOW. */
  expiry_date: string;
  /** The expiry this draft was prepared against. Differs only when someone edited it. */
  drafted_for_expiry: string;
  /**
   * ⚠ THE EXPIRY MOVED SINCE THIS DRAFT WAS PREPARED, so the body on screen states a date
   * the contract no longer has. The send REFUSES it (409). Surfaced rather than filtered
   * out — a draft that silently vanished is the invisible state this queue abolishes.
   */
  stale: boolean;
  days_remaining: number;
  /** The ledger BUCKET this draft closes — 60 | 30 | 7. */
  window_days: number;
  /** ⚠ `'•••'` for a seat without `amounts.view`. Never parse it — see the header. */
  line_total: string;
  currency: string;
  /** The customer already renewed. Shown, and refused by the send. */
  superseded: boolean;
  /**
   * ⚠ HOW MANY TIMES THIS CONTRACT'S **THIS WINDOW** HAS ALREADY BEEN EMAILED, at a
   * DIFFERENT expiry. The double-chase detector, and the scope is the whole of it.
   *
   * NOT "how many reminders has this customer had" — that count would be non-zero on every
   * routine 30- and 7-day card, because the 60/30/7 cadence guarantees it, and a warning
   * that fires on the normal case cannot mark the abnormal one. Under the routine cadence
   * each window fires exactly once per expiry, so this is 0, and the screen says so IN
   * WORDS. It becomes non-zero when an expiry was edited after a reminder went out:
   * migration 19 re-arms all three windows, the scan drafts that window again, and sending
   * it would put a second "expires in 30 days" letter in the same inbox.
   */
  same_window_sends: number;
  /** When the most recent of those went out. `null` when there have been none. */
  same_window_last_sent_at: string | null;
  recipient: RenewalDraftRecipient;
  mail: RenewalDraftMail;
  /**
   * ⚠ WHY THE SEND ROUTE WILL REFUSE THIS DRAFT OVER THE COUNTRY'S SIGN-OFF — `null` when
   * it will not. The letter closes with `Kind regards,` and the country's legal entity
   * name (owner ruling, 8 Aug 2026), and a placeholder or a multi-line value cannot be
   * signed off; the DRAFT still happens, only the SEND refuses (422
   * `LEGAL_ENTITY_UNUSABLE`).
   *
   * ⚠ THE SERVER HAS TO TELL US, AND IT IS NOT A CONVENIENCE. Standing rule 10 — gate the
   * control on what the ROUTE requires — and this client cannot answer the question
   * itself: nothing under `apps/web/src` reads `legal_entity` from the API. `COUNTRIES` in
   * `domain/countries.ts` is a HARDCODED mirror, so the switcher would keep displaying a
   * confident-looking company name while the stored row is the thing that is wrong.
   *
   * Not money, and not maskable: it is a defect name, never a value.
   */
  legal_entity_defect: 'unconfirmed' | 'multiline' | null;
  /** Why the LAST Send attempt failed. `null` on a draft nobody has tried. */
  send_error: string | null;
  created_at: string;
}

export interface RenewalDraftQueuePage {
  data: RenewalDraftRow[];
  /** The SERVER's count over the same predicate — never the length of `data`. */
  total: number;
  /** The COUNTRY's calendar day the server used. Never the browser's. */
  today: string;
  page: number;
  perPage: number;
}

/** What `POST /renewal-drafts/:id/send` answers on 200. */
export interface RenewalDraftSendResult {
  id: string;
  state: 'sent';
  /** ISO instant the ledger row was stamped with. */
  sent_at: string;
  /** The address it actually went to, echoed back so the screen states a fact. */
  to: string;
}

export interface RenewalDraftFilters {
  page?: number;
  perPage?: number;
}

/**
 * `RenewalSentRow` — renewal-sent.service.ts. ONE CUSTOMER REMINDER THAT LEFT THE BUILDING.
 *
 * ⚠ THERE IS NO MONEY FIELD AND NO `mail` ON THIS SHAPE, AND BOTH ABSENCES ARE THE POINT.
 * `line_total` would be a LIVE figure — a contract can be re-priced after a reminder goes
 * out — so a "historical" amount would be today's amount. And the letter is never stored:
 * `buildCustomerReminderMail` is pure and re-renders from CURRENT facts, so showing it under
 * a heading that says "sent" would present a live fact as a historical one. The API returns
 * neither, for every seat, which is a stronger guarantee than a correct mask.
 *
 * `party_name`, `product_name` and `recipient_name` are LIVE joins and are nullable: the
 * ledger carries the ids, and a party or product row that no longer exists (there is no
 * foreign key on either) or a deleted contact (`ON DELETE SET NULL`) leaves the name blank
 * rather than dropping the send from its own history. `recipient_email` and `expiry_date`
 * are the two SNAPSHOTS — where it actually went, and the date the letter actually stated.
 */
export interface RenewalSentRow {
  id: string;
  country_id: number;
  source: RenewalSource;
  line_id: string;
  party_id: string;
  party_name: string | null;
  product_id: string;
  product_name: string | null;
  /** The ledger BUCKET this send closed — 60 | 30 | 7. */
  window_days: number;
  /** SNAPSHOT — the expiry the letter stated, not the contract's expiry today. */
  expiry_date: string;
  recipient_contact_id: string | null;
  /** SNAPSHOT — the address it actually went to. */
  recipient_email: string | null;
  recipient_name: string | null;
  sent_at: string | null;
}

export interface RenewalSentHistoryPage {
  data: RenewalSentRow[];
  /** The SERVER's count over the same predicate — never the length of `data`. */
  total: number;
  today: string;
  page: number;
  perPage: number;
}

/** WHICH KIND OF NOTHING happened — `CLOSED_STATES`, renewal-sent.service.ts. */
export type RenewalClosedState = 'skipped' | 'superseded' | 'missed';

/**
 * `RenewalClosedRow` — one window that CLOSED WITHOUT A LETTER.
 *
 * ⚠ THE THIRD ANSWER, AND UNTIL NOW THERE WERE ONLY TWO. The queue's predicate is
 * `state = 'drafted'` and the history's is `state = 'sent'`; a completeness review ran both
 * in MySQL against every state the ledger writes and found `skipped`, `superseded` and
 * `missed` answering nothing on either. Those are customer-audience rows the scan writes on
 * purpose — the customer renewed early, the expiry passed before the scan ran again, or a
 * nearer notice went instead — so an operator saw two empty lists and concluded the customer
 * had never been due, when the system had decided never to chase them and recorded why.
 *
 * ⚠ NO RECIPIENT ON THIS SHAPE, AND THAT IS A FACT ABOUT THE ROW. These are written with a
 * null recipient because no contact is ever resolved — no letter is prepared — so a "Sent to"
 * column would be permanently empty, and an empty cell reads as "we do not know where it
 * went" rather than "nothing went anywhere". No money field either, for the same reason
 * {@link RenewalSentRow} has none.
 */
export interface RenewalClosedRow {
  id: string;
  country_id: number;
  source: RenewalSource;
  line_id: string;
  party_id: string;
  party_name: string | null;
  product_id: string;
  product_name: string | null;
  window_days: number;
  expiry_date: string;
  state: RenewalClosedState;
  created_at: string | null;
}

export interface RenewalClosedHistoryPage {
  data: RenewalClosedRow[];
  total: number;
  today: string;
  page: number;
  perPage: number;
}

export interface RenewalSentFilters {
  page?: number;
  perPage?: number;
  /**
   * ⚠ A PARTY ID GOES IN THE PATH, NOT THE QUERY STRING, and this field is what chooses the
   * ROUTE rather than a parameter appended to one. `@CountryScoped(Party)` makes the guard
   * load that party's `country_id` before the handler runs; a query-string id would have
   * been invisible to the bootstrap validator and to the guard alike.
   */
  party?: string;
}

/* ───────────────────────────────────────────────────────────────── fetchers ── */

/** `GET /countries/:cc/renewal-drafts` — `quotes.view`, read-only. */
export async function fetchRenewalDrafts(
  countryCode: string,
  filters: RenewalDraftFilters = {},
  signal?: AbortSignal,
): Promise<RenewalDraftQueuePage> {
  return apiRequest<RenewalDraftQueuePage>(`/countries/${countryCode}/renewal-drafts`, {
    query: { page: filters.page, perPage: filters.perPage },
    signal,
  });
}

/**
 * `POST /countries/:cc/renewal-drafts/:id/send` — `quotes.view` AND `quotes.send`.
 *
 * ⚠ THE ONE WRITE THIS FEATURE HAS, AND IT PUTS A LETTER IN A CUSTOMER'S INBOX. It carries
 * no body: everything the mail needs is re-derived server-side from the same query the
 * reviewer's row came from, which is what makes "what you approved is what goes out" true
 * rather than hopeful. A body here would be somewhere for a client to put different words.
 */
export async function sendRenewalDraft(
  countryCode: string,
  draftId: string,
): Promise<RenewalDraftSendResult> {
  return apiRequest<RenewalDraftSendResult>(
    `/countries/${countryCode}/renewal-drafts/${draftId}/send`,
    { method: 'POST' },
  );
}

/**
 * `GET /countries/:cc/renewal-reminders/sent` — or its per-party sibling — `quotes.view`.
 *
 * ⚠ SAME KEY AS THE QUEUE, AND THAT IS THE ROUTE'S OWN KEY READ FROM THE CONTROLLER. The
 * tempting wrong gate is `quotes.send`, and it is wrong for the reason the queue's gate is:
 * ACCOUNTS holds `quotes.view` and NOT `quotes.send`, already reads the draft queue
 * including every recipient address, and chasing money is exactly the job that needs to know
 * whether the customer has already been contacted.
 *
 * ⚠ A MALFORMED `party` CANNOT SILENTLY WIDEN THE LIST. Anything that is not a run of digits
 * is not a party id, so it is not sent — and the caller is told, by getting back the
 * country-wide list with no party filter claimed. Appending an unusable value to the
 * country-wide route and calling the result "filtered" is the invisible state rule 9 forbids:
 * an operator would read a whole country's sends as one customer's and stop chasing.
 */
export function isPartyFilter(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^\d+$/.test(value);
}

export async function fetchSentReminders(
  countryCode: string,
  filters: RenewalSentFilters = {},
  signal?: AbortSignal,
): Promise<RenewalSentHistoryPage> {
  const path = isPartyFilter(filters.party)
    ? `/countries/${countryCode}/parties/${filters.party}/renewal-reminders/sent`
    : `/countries/${countryCode}/renewal-reminders/sent`;
  return apiRequest<RenewalSentHistoryPage>(path, {
    query: { page: filters.page, perPage: filters.perPage },
    signal,
  });
}

/**
 * `GET /countries/:cc/renewal-reminders/closed` — or its per-party sibling — `quotes.view`.
 *
 * ⚠ THE SAME KEY AS THE OTHER TWO, READ FROM THE CONTROLLER. This read carries no recipient,
 * no letter and no money, so it is strictly less sensitive than the queue Accounts already
 * sees. Gating it harder would hide from the seat that chases money the one fact that
 * explains a silence they are chasing.
 *
 * The party goes in the PATH for the identical reason it does on the sent route: a
 * query-string id gets no bootstrap validation and no `@CountryScoped(Party)` coverage.
 */
export async function fetchClosedReminders(
  countryCode: string,
  filters: RenewalSentFilters = {},
  signal?: AbortSignal,
): Promise<RenewalClosedHistoryPage> {
  const path = isPartyFilter(filters.party)
    ? `/countries/${countryCode}/parties/${filters.party}/renewal-reminders/closed`
    : `/countries/${countryCode}/renewal-reminders/closed`;
  return apiRequest<RenewalClosedHistoryPage>(path, {
    query: { page: filters.page, perPage: filters.perPage },
    signal,
  });
}

/* ────────────────────────────────────────────────────────────────── errors ── */

/**
 * Re-authored per surface, the house pattern — and this one differs from
 * `renewalsErrorText` in the case that matters.
 *
 * ⚠ A 409 IS PASSED THROUGH VERBATIM, AND DELIBERATELY. The three refusals a send can meet
 * — the transport refused, the customer already renewed, the expiry moved — are each
 * answered by a server sentence that says WHAT happened and, in every case, that the
 * customer was NOT contacted. Replacing those with a house phrase would delete the only
 * text that answers the operator's actual question, which is "did it go out or not".
 *
 * A 403 still never names a permission key.
 */
export function draftsErrorText(error: unknown): string {
  if (error instanceof ApiTransportError) {
    return 'The server could not be reached. Nothing was sent.';
  }
  if (error instanceof ApiRequestError) {
    if (error.code === 'COUNTRY_SCOPE_VIOLATION') {
      return 'That queue belongs to another country.';
    }
    if (error.status === 403) {
      return 'This account is not allowed to do that in this country.';
    }
    if (error.status === 404) {
      return 'That draft is no longer waiting to be sent. It may already have gone out.';
    }
    return error.message;
  }
  return 'Something went wrong. Nothing was sent.';
}

/* ─────────────────────────────────────────────────────────── query keys ── */

export const renewalDraftKeys = {
  queue: (cc: string, filters: RenewalDraftFilters) =>
    ['renewals', cc, 'drafts', filters] as const,
  /**
   * ⚠ NOT NESTED UNDER `'drafts'`. The send mutation invalidates `['renewals', cc, 'drafts']`
   * as a prefix, and a sent-history key under it would be swept by that too — which happens
   * to be what we want after a send, but only by accident. It is its own branch and the
   * mutation invalidates BOTH explicitly, so the refresh is a decision rather than a
   * side-effect of how somebody named a key.
   */
  sent: (cc: string, filters: RenewalSentFilters) => ['renewals', cc, 'sent', filters] as const,
  /** Its own branch again, for the same reason `sent` is not nested under `drafts`. */
  closed: (cc: string, filters: RenewalSentFilters) =>
    ['renewals', cc, 'closed', filters] as const,
};
